import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ChevronDown, Grid3X3 } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Skeleton } from '../components/ui/skeleton';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROWS_PER_PAGE = 10;
const ALPHABET = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const CategoryItem = ({ cat, isActive, onSelect, onSubSelect, activeSearch }) => {
  const hasSubs = cat.subcategories && cat.subcategories.length > 0;
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`category-${cat.id}`}>
      <button onClick={() => { if (hasSubs) setOpen(!open); onSelect(cat.id); }}
        className={`w-full text-left px-3 py-2 text-sm transition-all flex items-center justify-between ${
          isActive ? 'bg-fire text-white font-bold' : 'text-white/70 hover:text-white hover:bg-white/10'
        }`}>
        <span>{cat.name}</span>
        {hasSubs && <ChevronDown className={`w-3 h-3 opacity-50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />}
      </button>
      {hasSubs && (
        <div
          className={`overflow-hidden transition-all duration-200 ease-in-out ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
          onMouseLeave={() => setOpen(false)}>
          <div className="bg-black/40 border-l-2 border-fire/40 ml-2">
            {cat.subcategories.map((sub) => (
              <button key={sub.id} onClick={() => onSubSelect(cat.id, sub.search)}
                className={`w-full text-left px-4 py-1.5 text-xs transition-all ${
                  activeSearch === sub.search ? 'bg-fire text-white font-bold' : 'text-white/50 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`subcategory-${sub.id}`}>
                {sub.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const GRID_OPTIONS = [
  { cols: 5, label: '5' },
  { cols: 6, label: '6' },
  { cols: 7, label: '7' },
];

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [gridCols, setGridCols] = useState(5);

  const itemsPerPage = gridCols * ROWS_PER_PAGE;

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const letter = searchParams.get('letter') || '';
  const page = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    const searchTerm = searchParams.get('search');
    const catObj = categories.find(c => c.id === category);
    const pageNum = parseInt(searchParams.get('page') || '1');

    // NOINDEX: search pages, alphabet-filter pages, paginated pages beyond page 1
    // These are low-value/duplicate for Google and waste crawl budget
    const isLowValue = !!searchTerm || !!letter || pageNum > 1;

    if (isLowValue) {
      // Set noindex for search/filter/paginated pages
      const title = searchTerm
        ? `Search: "${searchTerm}" | PosterFire`
        : `Page ${pageNum} | PosterFire`;
      SEO.applyNoIndex(title, '/shop');
    } else if (catObj) {
      // INDEX: category pages (page 1 only)
      SEO.applyCategorySEO(catObj, category);
    } else {
      // INDEX: main shop page (page 1, no filters)
      document.title = 'Shop All Posters | PosterFire — Premium Wall Art & Prints';
      SEO.setCanonical(`${SEO.CANONICAL_BASE}/shop`);
      SEO.setRobots('index, follow');
      SEO.setMeta('meta[name="description"]', 'content', 'Shop 40,000+ premium posters, wall art & home decor prints. Movie posters, music art, sports, abstract & more. Free shipping over $50.');
      SEO.setMeta('meta[property="og:title"]', 'content', document.title, 'meta', { property: 'og:title' });
      SEO.setMeta('meta[property="og:url"]', 'content', `${SEO.CANONICAL_BASE}/shop`, 'meta', { property: 'og:url' });
      SEO.setMeta('meta[property="og:type"]', 'content', 'website', 'meta', { property: 'og:type' });
    }

    return () => SEO.resetDefaults();
  }, [searchParams, category, letter, categories]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.post(`${API}/track-visit`).catch(() => {});
    // Try to get visitor geo for the world map
    fetch('https://ipapi.co/json/').then(r => r.json()).then(data => {
      if (data.country_code) {
        axios.post(`${API}/track-visit-geo`, {
          ip: data.ip,
          country_code: data.country_code,
          country_name: data.country_name,
          city: data.city || '',
          region: data.region || '',
          latitude: data.latitude || 0,
          longitude: data.longitude || 0
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const skip = (page - 1) * itemsPerPage;
        const params = new URLSearchParams({ limit: itemsPerPage, skip });
        if (category) params.set('category', category);
        if (search) params.set('search', search);
        if (letter) params.set('letter', letter);
        const [productsRes, countRes] = await Promise.all([
          axios.get(`${API}/products?${params}`),
          axios.get(`${API}/products/count?${new URLSearchParams(
            Object.fromEntries([category && ['category', category], search && ['search', search], letter && ['letter', letter]].filter(Boolean))
          )}`)
        ]);
        setProducts(productsRes.data);
        setTotalCount(countRes.data.count);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, search, letter, page, itemsPerPage]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  };

  const handleSubcategorySelect = (categoryId, searchTerm) => {
    const params = new URLSearchParams();
    params.set('category', categoryId);
    params.set('search', searchTerm);
    params.set('page', '1');
    setSearchParams(params);
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateFilter('page', newPage.toString());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Grid class mapping
  const gridClass = {
    5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
    6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6',
    7: 'grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7',
  }[gridCols];

  return (
    <div className="min-h-screen fire-gradient" data-testid="shop-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-52 min-h-screen bg-black/80 backdrop-blur-sm p-3 sticky top-0 hidden lg:block" data-testid="category-sidebar">
          <h2 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Categories</h2>
          <nav className="space-y-0.5">
            <button onClick={() => setSearchParams({})}
              className={`w-full text-left px-3 py-1.5 text-sm transition-all ${
                !category && !search ? 'bg-fire text-white font-bold' : 'text-white/70 hover:text-white hover:bg-white/10'
              }`} data-testid="category-all">
              All Posters
            </button>
            {categories.filter(c => c.visible_sidebar !== false).map((cat) => (
              <CategoryItem key={cat.id} cat={cat}
                isActive={category === cat.id && !search}
                activeSearch={category === cat.id ? search : ''}
                onSelect={(id) => { const p = new URLSearchParams(); p.set('category', id); p.set('page', '1'); setSearchParams(p); }}
                onSubSelect={handleSubcategorySelect} />
            ))}
          </nav>
          <div className="mt-4 pt-3 border-t border-white/20">
            <p className="text-white/50 text-xs">{totalCount.toLocaleString()} posters</p>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 px-3 lg:px-4 pt-2 pb-6">
          {/* Mobile Category Select */}
          <div className="lg:hidden mb-3">
            <select value={category} onChange={(e) => { const p = new URLSearchParams(); if (e.target.value) p.set('category', e.target.value); p.set('page', '1'); setSearchParams(p); }}
              className="w-full bg-black/50 text-white border border-white/20 py-2 px-3 text-sm" data-testid="mobile-category-select">
              <option value="">All Posters</option>
              {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
          </div>

          {/* SEO H1 */}
          <h1 className="sr-only" data-testid="shop-h1">
            {search ? `Search results for "${search}"` :
             category ? `${categories.find(c => c.id === category)?.name || category} Posters` :
             'Shop All Posters — PosterFire Premium Wall Art'}
          </h1>

          {/* Top Bar: Alphabet + Grid + Pagination in one seamless bar */}
          <div className="mb-3">
            <div className="bg-black/40 backdrop-blur-sm px-3 py-2 rounded" data-testid="alphabet-filter">
              <div className="flex flex-wrap gap-[3px] justify-center items-center">
                {ALPHABET.map((char) => (
                  <button key={char} onClick={() => updateFilter('letter', letter === char ? '' : char)}
                    className={`w-8 h-8 flex items-center justify-center font-heading font-bold text-xs transition-all ${
                      letter === char ? 'bg-fire text-white' : 'bg-black/40 text-white/60 hover:bg-fire/50 hover:text-white'
                    }`} data-testid={`letter-${char}`}>
                    {char}
                  </button>
                ))}
                {letter && (
                  <button onClick={() => updateFilter('letter', '')}
                    className="px-3 h-8 bg-white/20 text-white text-xs hover:bg-white/30 ml-1" data-testid="clear-letter">
                    Clear
                  </button>
                )}

                {/* Divider */}
                <div className="w-px h-6 bg-white/20 mx-1.5" />

                {/* Grid Selector */}
                <div className="flex items-center gap-[3px]" data-testid="grid-selector">
                  <Grid3X3 className="w-3.5 h-3.5 text-white/40 mr-0.5" />
                  {GRID_OPTIONS.map((opt) => (
                    <button key={opt.cols} onClick={() => setGridCols(opt.cols)}
                      className={`w-8 h-8 flex items-center justify-center text-xs font-bold transition-all ${
                        gridCols === opt.cols ? 'bg-fire text-white' : 'bg-black/40 text-white/50 hover:text-white hover:bg-white/10'
                      }`} data-testid={`grid-${opt.cols}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Pagination */}
                {!loading && totalCount > itemsPerPage && (
                  <>
                    <div className="w-px h-6 bg-white/20 mx-1.5" />
                    <button onClick={() => goToPage(page - 1)} disabled={page <= 1}
                      className="w-8 h-8 flex items-center justify-center bg-black/40 text-white disabled:opacity-20 hover:bg-fire/60 transition-colors"
                      data-testid="prev-page">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-white/70 text-xs font-bold px-1.5 min-w-[50px] text-center">
                      {page}/{totalPages}
                    </span>
                    <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                      className="w-8 h-8 flex items-center justify-center bg-black/40 text-white disabled:opacity-20 hover:bg-fire/60 transition-colors"
                      data-testid="next-page">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Filter info */}
            {(search || (category && !search)) && (
              <div className="mt-1.5 px-1">
                {search && <span className="text-white/60 text-xs">Filtering: "{search}"</span>}
                {!search && category && <span className="text-white/60 text-xs">{categories.find(c => c.id === category)?.name}</span>}
              </div>
            )}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className={`grid ${gridClass} gap-2`}>
              {[...Array(itemsPerPage)].map((_, i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="aspect-[3/4] w-full bg-white/10" />
                  <Skeleton className="h-3 w-12 bg-white/10" />
                  <Skeleton className="h-3 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-black/30 rounded" data-testid="no-products">
              <h3 className="font-heading text-xl text-white mb-3">No posters found</h3>
              <p className="text-white/60 mb-6 text-sm">Try a different category or filter.</p>
              <button onClick={() => setSearchParams({})}
                className="px-5 py-2 bg-fire text-white text-sm font-bold uppercase tracking-wider hover:bg-fire-light transition-colors">
                Clear Filters
              </button>
            </div>
          ) : (
            <div className={`grid ${gridClass} gap-2`} data-testid="products-grid">
              {products.map((product, i) => (
                <div key={product.id} className="animate-fade-in" style={{ animationDelay: `${i * 0.015}s` }}>
                  <ProductCard product={product} index={i} />
                </div>
              ))}
            </div>
          )}

          {/* Bottom pagination */}
          {!loading && totalCount > itemsPerPage && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1}
                className="px-4 py-2 bg-black/40 text-white text-xs font-medium disabled:opacity-20 hover:bg-fire/60 transition-colors rounded"
                data-testid="prev-page-bottom">
                <ChevronLeft className="w-4 h-4 inline mr-1" />Prev
              </button>
              <span className="text-white/60 text-xs">
                {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, totalCount)} of {totalCount.toLocaleString()}
              </span>
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages}
                className="px-4 py-2 bg-black/40 text-white text-xs font-medium disabled:opacity-20 hover:bg-fire/60 transition-colors rounded"
                data-testid="next-page-bottom">
                Next<ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ShopPage;
